import {Paper, Group, Stack, Text, useMantineTheme} from "@mantine/core";
import {ArticleMarketplaceStatus, MarketplaceName} from "../../../interfaces/response/MarketplaceStatus";
import classes from "./MarketplaceStats.module.css";

/**
 * Props per il componente MarketplaceStats
 */

interface MarketplaceStatsProps {
    /** Dati degli articoli per calcolare le statistiche */
    data?: ArticleMarketplaceStatus[];
}

/**
 * Componente che visualizza le statistiche aggregate per ogni marketplace.
 * Mostra:
 * - Numero totale articoli
 * - Per ogni marketplace: abilitati, in feed, pubblicati, vendibili
 * 
 * Le card sono scorrevoli orizzontalmente per supportare molti marketplace.
 */

const MarketplaceStats = ({data}: MarketplaceStatsProps) => {
    const theme = useMantineTheme();

    if (!data) {
        return null;
    }

    /**
     * Calcola le statistiche per un marketplace specifico
     * @param marketplace - Nome del marketplace
     * @returns Oggetto con conteggi
     */
    const calculateStats = (marketplace: MarketplaceName) => {
        // Verifica se il feed è supportato per questo marketplace (basta controllare il primo articolo)
        const feedSupported = data.length > 0 
            ? data[0].marketplaceStatuses[marketplace]?.feedStatus?.available 
            : true;
        
        // Verifica se il rilevamento sospesi è supportato (solo Mirakl)
        const suspendedDetectionSupported = data.length > 0 
            ? data[0].marketplaceStatuses[marketplace]?.publicationStatus?.suspendedDetectionAvailable 
            : false;

        const enabledTotal = data.filter(a => a.marketplaceStatuses[marketplace]?.enabledInBackoffice);
        
        // Abilitati e disponibili a magazzino
        const enabledAvailable = enabledTotal.filter(a => a.availability).length;
        // Abilitati ma NON disponibili a magazzino
        const enabledUnavailable = enabledTotal.filter(a => !a.availability).length;

        const inFeed = data.filter(a => a.marketplaceStatuses[marketplace]?.feedStatus?.present).length;
        const published = data.filter(a => a.marketplaceStatuses[marketplace]?.publicationStatus?.published).length;
        const sellable = data.filter(a => a.marketplaceStatuses[marketplace]?.publicationStatus?.sellable).length;
        
        // Statistiche Mirakl-specifiche
        const suspended = data.filter(a => a.marketplaceStatuses[marketplace]?.publicationStatus?.suspended).length;
        const invalidData = data.filter(a => a.marketplaceStatuses[marketplace]?.publicationStatus?.invalidData).length;

        return {
            enabledAvailable, 
            enabledUnavailable, 
            inFeed, 
            published, 
            sellable,
            suspended,
            invalidData,
            feedSupported,
            suspendedDetectionSupported
        };
    };

    // Lista dei marketplace da visualizzare
    const marketplaces: { name: MarketplaceName; label: string }[] = [
        {name: 'EBAY', label: 'eBay'},
        {name: 'MEDIAWORLD', label: 'Mediaworld'},
        {name: 'LEROY_MERLIN', label: 'Leroy Merlin'},
        {name: 'KELKOO', label: 'Kelkoo'},
        {name: 'EPRICE', label: 'ePrice'}
    ];

    return (
        <div className={classes.statsContainer}>
            {/* Card totale articoli */}
            <Paper className={classes.statCard} withBorder>
                <Stack gap={2}>
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase">Articoli Totali</Text>
                    <Text size="lg" fw={700} c={theme.colors.textBlack[0]}>{data.length}</Text>
                </Stack>
            </Paper>

            {/* Card per ogni marketplace */}
            {marketplaces.map(({name, label}) => {
                const stats = calculateStats(name);
                return (
                    <Paper key={name} className={classes.statCard} withBorder>
                        <Stack gap={6}>
                            <Text size="xs" c="dimmed" fw={600} tt="uppercase">{label}</Text>
                            <Group gap="md" justify="space-between">
                                <div className={classes.statItem}>
                                    <Text size="10px" c="dimmed" fw={500}>ABILITATI</Text>
                                    <Group gap={3} align="baseline">
                                        <Text size="sm" fw={600} c={theme.colors.blue?.[6] || 'blue'} title="Disponibili">
                                            {stats.enabledAvailable}
                                        </Text>
                                        <Text size="10px" c="dimmed">/</Text>
                                        <Text size="xs" fw={500} c="red" title="Non disponibili">
                                            {stats.enabledUnavailable}
                                        </Text>
                                    </Group>
                                </div>
                                <div className={classes.statItem}>
                                    <Text size="10px" c="dimmed" fw={500}>IN FEED</Text>
                                    {stats.feedSupported ? (
                                        <Text size="sm" fw={600} c={theme.colors.orange?.[6] || 'orange'}>{stats.inFeed}</Text>
                                    ) : (
                                        <Text size="sm" fw={600} c="dimmed" title="Feed non supportato">-</Text>
                                    )}
                                </div>
                                <div className={classes.statItem}>
                                    <Text size="10px" c="dimmed" fw={500}>PUBBLICATI</Text>
                                    <Text size="sm" fw={600} c={theme.colors.green?.[6] || 'green'}>{stats.published}</Text>
                                </div>
                                <div className={classes.statItem}>
                                    <Text size="10px" c="dimmed" fw={500}>VENDIBILI</Text>
                                    <Text size="sm" fw={600} c={theme.colors.teal?.[6] || 'teal'}>{stats.sellable}</Text>
                                </div>
                                {stats.suspendedDetectionSupported && (
                                    <>
                                        <div className={classes.statItem}>
                                            <Text size="10px" c="dimmed" fw={500}>SOSPESI</Text>
                                            <Text size="sm" fw={600} c="red">{stats.suspended}</Text>
                                        </div>
                                        <div className={classes.statItem}>
                                            <Text size="10px" c="dimmed" fw={500}>DATI INVALIDI</Text>
                                            <Text size="sm" fw={600} c="orange">{stats.invalidData}</Text>
                                        </div>
                                    </>
                                )}
                            </Group>
                        </Stack>
                    </Paper>
                );
            })}
        </div>
    );
};

export default MarketplaceStats;
